import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import TargetProductionModal from '../../components/TargetProductionModal';
import DailyReportModal from '../../components/DailyReportModal';
import WeeklyReportModal from '../../components/WeeklyReportModal';
import MonthlyReportModal from '../../components/MonthlyReportModal';
import './Enterprise.css';
import {
  LineChart, Line, PieChart, Pie, Cell, Bar,
  ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Area
} from 'recharts';
import { fetchCompanyDashboard, updateTargetProduction } from '../../services/api';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

// 도넛 차트 컴포넌트 (memo로 최적화, 외부 정의로 재생성 방지)
const DonutChart = memo(({ data }) => {
  const { running, idle, stopped } = data;
  const total = running + idle + stopped;
  const percentage = total > 0 ? ((running / total) * 100).toFixed(0) : 0;

  const pieData = [
    { name: '가동', value: running, color: '#22c55e' },
    { name: '대기', value: idle, color: '#fbbf24' },
    { name: '정지', value: stopped, color: '#ef4444' }
  ];

  // 데이터 기반 key로 동일 데이터일 때 재애니메이션 방지
  const chartKey = `${running}-${idle}-${stopped}`;

  return (
    <div className="factory-donut">
      <PieChart width={80} height={80}>
        <Pie
          key={chartKey}
          data={pieData}
          cx="50%"
          cy="50%"
          innerRadius={20}
          outerRadius={35}
          paddingAngle={0}
          dataKey="value"
          stroke="none"
          isAnimationActive={false}
        >
          {pieData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
          ))}
        </Pie>
        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="12" fontWeight="bold">
          {percentage}%
        </text>
      </PieChart>
      <div className="donut-legend">
        <div className="donut-legend-item">
          <div className="legend-dot green"></div>
          <span>{running}</span>
        </div>
        <div className="donut-legend-item">
          <div className="legend-dot yellow"></div>
          <span>{idle}</span>
        </div>
        <div className="donut-legend-item">
          <div className="legend-dot red"></div>
          <span>{stopped}</span>
        </div>
      </div>
    </div>
  );
});

// 숫자 카운트 애니메이션 훅
const useCountAnimation = (end, duration = 1000) => {
  const [count, setCount] = useState(end);
  const [prevEnd, setPrevEnd] = useState(end);

  useEffect(() => {
    // duration이 0이면 즉시 값 설정 (애니메이션 없음)
    if (duration === 0) {
      setCount(end);
      setPrevEnd(end);
      return;
    }

    if (end === prevEnd) return;

    const start = prevEnd;
    const increment = (end - start) / (duration / 16);
    let current = start;

    const timer = setInterval(() => {
      current += increment;
      if ((increment > 0 && current >= end) || (increment < 0 && current <= end)) {
        setCount(end);
        setPrevEnd(end);
        clearInterval(timer);
      } else {
        setCount(current);
      }
    }, 16);

    return () => clearInterval(timer);
  }, [end, prevEnd, duration]);

  return count;
};

function Enterprise() {
  const navigate = useNavigate();

  // 상태 관리
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const refreshIndicatorRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTargetProduction, setCurrentTargetProduction] = useState(0);
  const [isDailyReportModalOpen, setIsDailyReportModalOpen] = useState(false);
  const [isWeeklyReportModalOpen, setIsWeeklyReportModalOpen] = useState(false);
  const [isMonthlyReportModalOpen, setIsMonthlyReportModalOpen] = useState(false);
  // 데이터 로드 함수
  const loadDashboardData = useCallback(async (isAutoRefresh = false) => {
    try {
      if (isAutoRefresh) {
        // CSS 클래스로 애니메이션 처리 (state 대신 ref 사용으로 리렌더링 방지)
        if (refreshIndicatorRef.current) {
          refreshIndicatorRef.current.classList.add('refreshing');
          setTimeout(() => {
            refreshIndicatorRef.current?.classList.remove('refreshing');
          }, 1500);
        }
      } else {
        setLoading(true);
      }
      setError(null);
      const data = await fetchCompanyDashboard();
      setDashboardData(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message);
      console.error('Dashboard data loading failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 초기 데이터 로드
  useEffect(() => {
    loadDashboardData(false);
  }, [loadDashboardData]);

  // useAutoRefresh 훅 사용 (30초 쿨다운 적용됨)
  useAutoRefresh(() => {
    loadDashboardData(true);
  });

  // 기본값 설정 (로딩 중이거나 에러 발생 시)
  const kpiCards = dashboardData?.kpiCards || {
    totalFactories: 0,
    totalEquipment: 0,
    runningEquipment: 0,
    idleEquipment: 0,
    stoppedEquipment: 0,
    operatingRate: 0,
    dailyProduction: 0,
    energyConsumption: 0
  };

  const oeeMetrics = dashboardData?.oeeMetrics || {
    oee: 0,
    availability: 0,
    performance: 0,
    quality: 0
  };

  // 첫 로드 완료 여부 추적
  const [isFirstLoadComplete, setIsFirstLoadComplete] = useState(false);

  useEffect(() => {
    if (dashboardData && !isFirstLoadComplete) {
      setIsFirstLoadComplete(true);
    }
  }, [dashboardData, isFirstLoadComplete]);

  // 숫자 카운트 애니메이션 적용
  const animatedTotalFactories = useCountAnimation(kpiCards.totalFactories, isFirstLoadComplete ? 1000 : 0);
  const animatedTotalEquipment = useCountAnimation(kpiCards.totalEquipment, isFirstLoadComplete ? 1000 : 0);
  const animatedRunningEquipment = useCountAnimation(kpiCards.runningEquipment, isFirstLoadComplete ? 1000 : 0);
  const animatedIdleEquipment = useCountAnimation(kpiCards.idleEquipment, isFirstLoadComplete ? 1000 : 0);
  const animatedStoppedEquipment = useCountAnimation(kpiCards.stoppedEquipment, isFirstLoadComplete ? 1000 : 0);
  const animatedOperatingRate = useCountAnimation(kpiCards.operatingRate, isFirstLoadComplete ? 1000 : 0);
  const animatedDailyProduction = useCountAnimation(kpiCards.dailyProduction, isFirstLoadComplete ? 1000 : 0);
  const animatedEnergyConsumption = useCountAnimation(kpiCards.energyConsumption, isFirstLoadComplete ? 1000 : 0);

  // OEE 메트릭 애니메이션
  const animatedOee = useCountAnimation(oeeMetrics.oee, isFirstLoadComplete ? 1000 : 0);
  const animatedAvailability = useCountAnimation(oeeMetrics.availability, isFirstLoadComplete ? 1000 : 0);
  const animatedPerformance = useCountAnimation(oeeMetrics.performance, isFirstLoadComplete ? 1000 : 0);
  const animatedQuality = useCountAnimation(oeeMetrics.quality, isFirstLoadComplete ? 1000 : 0);

  const factoryStatuses = dashboardData?.factoryStatuses || [];

  // 생산 추이 차트 데이터 변환 (90일 고정, 최신이 오른쪽 끝, 왼쪽은 과거)
  const productionTrendData = useMemo(() => {
    const result = [];
    const daysToShow = 90;

    // 오늘 날짜를 기준으로 90일 전부터 계산
    const today = new Date();

    if (dashboardData?.productionTrend) {
      // 실제 데이터가 있는 경우
      const actualDataLength = dashboardData.productionTrend.dates.length;

      // 90일 전체 생성
      for (let i = 0; i < daysToShow; i++) {
        // i=0은 89일 전, i=89는 오늘
        const dateObj = new Date(today);
        dateObj.setDate(today.getDate() - (daysToShow - 1 - i));
        const dateStr = `${dateObj.getMonth() + 1}월 ${dateObj.getDate()}일`;

        if (i < (daysToShow - actualDataLength)) {
          // 데이터가 없는 과거 날들 (0으로 채움)
          result.push({
            day: i + 1,
            date: dateStr,
            생산량: 0,
            가동률: 0
          });
        } else {
          // 실제 데이터가 있는 날 (과거 → 최신 순서)
          const actualIndex = i - (daysToShow - actualDataLength);
          result.push({
            day: i + 1,
            date: dateStr,
            생산량: dashboardData.productionTrend.productionCounts[actualIndex],
            가동률: Number(dashboardData.productionTrend.operatingRates[actualIndex].toFixed(1))
          });
        }
      }
    } else {
      // 데이터가 전혀 없는 경우 90일 전체를 0으로
      for (let i = 0; i < daysToShow; i++) {
        const dateObj = new Date(today);
        dateObj.setDate(today.getDate() - (daysToShow - 1 - i));
        const dateStr = `${dateObj.getMonth() + 1}월 ${dateObj.getDate()}일`;

        result.push({
          day: i + 1,
          date: dateStr,
          생산량: 0,
          가동률: 0
        });
      }
    }

    return result;
  }, [dashboardData?.productionTrend]);

  // OEE 추이 차트 데이터 변환 (90일 고정, 최신이 오른쪽 끝, 왼쪽은 과거)
  const oeeMonthlyData = useMemo(() => {
    const result = [];
    const daysToShow = 90;

    // 오늘 날짜를 기준으로 90일 전부터 계산
    const today = new Date();

    if (dashboardData?.oeeTrend) {
      // 실제 데이터가 있는 경우
      const actualDataLength = dashboardData.oeeTrend.dates.length;

      // 90일 전체 생성
      for (let i = 0; i < daysToShow; i++) {
        // i=0은 89일 전, i=89는 오늘
        const dateObj = new Date(today);
        dateObj.setDate(today.getDate() - (daysToShow - 1 - i));
        const dateStr = `${dateObj.getMonth() + 1}월 ${dateObj.getDate()}일`;

        if (i < (daysToShow - actualDataLength)) {
          // 데이터가 없는 과거 날들 (0으로 채움)
          result.push({
            day: i + 1,
            date: dateStr,
            'OEE': 0,
            '가용성': 0,
            '성능': 0,
            '품질': 0
          });
        } else {
          // 실제 데이터가 있는 날 (과거 → 최신 순서)
          const actualIndex = i - (daysToShow - actualDataLength);
          result.push({
            day: i + 1,
            date: dateStr,
            'OEE': Number(dashboardData.oeeTrend.oee[actualIndex].toFixed(1)),
            '가용성': Number(dashboardData.oeeTrend.availability[actualIndex].toFixed(1)),
            '성능': Number(dashboardData.oeeTrend.performance[actualIndex].toFixed(1)),
            '품질': Number(dashboardData.oeeTrend.quality[actualIndex].toFixed(1))
          });
        }
      }
    } else {
      // 데이터가 전혀 없는 경우 90일 전체를 0으로
      for (let i = 0; i < daysToShow; i++) {
        const dateObj = new Date(today);
        dateObj.setDate(today.getDate() - (daysToShow - 1 - i));
        const dateStr = `${dateObj.getMonth() + 1}월 ${dateObj.getDate()}일`;

        result.push({
          day: i + 1,
          date: dateStr,
          'OEE': 0,
          '가용성': 0,
          '성능': 0,
          '품질': 0
        });
      }
    }

    return result;
  }, [dashboardData?.oeeTrend]);

  // 공장 데이터 변환
  const factoryData = factoryStatuses.map((factory, index) => ({
    id: factory.factoryId || factory.id,
    name: factory.factoryName,
    location: factory.factoryCode,
    equipment: {
      total: factory.totalEquipment,
      running: factory.runningEquipment,
      idle: factory.idleEquipment,
      stopped: factory.stoppedEquipment
    },
    operationRate: factory.operatingRate,
    production: factory.production,
    alarms: factory.alarmCount,
    // 환경 데이터 (더미)
    environment: {
      temperature: [23.5, 24.2, 22.8, 25.1, 23.0][index % 5],
      humidity: [55, 62, 48, 58, 52][index % 5],
      airQuality: [12, 18, 25, 8, 32][index % 5]
    }
  }));

  // 공장 카드 클릭 핸들러
  const handleFactoryClick = (factoryId) => {
    navigate(`/factory?factoryId=${factoryId}`);
  };

  // 목표 생산량 설정 모달 열기
  const handleOpenModal = () => {
    setCurrentTargetProduction(dashboardData?.kpiCards?.dailyProduction || 0);
    setIsModalOpen(true);
  };

  // 목표 생산량 저장
  const handleSaveTargetProduction = async (value) => {
    try {
      await updateTargetProduction(value);
      // 성공 시 대시보드 데이터 새로고침
      await loadDashboardData(false);
      setIsModalOpen(false);
    } catch (err) {
      console.error('Failed to update target production:', err);
      throw err;
    }
  };

  // 반원형 게이지 컴포넌트
  const SemiCircleGauge = ({ value, color, title, subtitle, onSettingsClick }) => {
    const percentage = (value / 100) * 100; // 0-100 스케일로 정규화
    const circleSize = 120;

    return (
      <div className="oee-card">
        <div className="oee-title-container">
          <div className="oee-title">{title}</div>
          {onSettingsClick && (
            <button className="oee-settings-icon" onClick={onSettingsClick} title="목표 생산량 설정">
              <span className="material-symbols-outlined">settings</span>
            </button>
          )}
        </div>
        <div className="gauge-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <svg width={circleSize} height={circleSize/2 + 5} style={{ display: 'block' }}>
            {/* 배경 호 */}
            <path
              d={`M 15 ${circleSize/2} A 45 45 0 0 1 ${circleSize - 15} ${circleSize/2}`}
              fill="none"
              stroke="#374151"
              strokeWidth="12"
              strokeLinecap="round"
            />
            {/* 값 호 */}
            <path
              d={`M 15 ${circleSize/2} A 45 45 0 0 1 ${circleSize - 15} ${circleSize/2}`}
              fill="none"
              stroke={color}
              strokeWidth="12"
              strokeLinecap="round"
              strokeDasharray="141"
              strokeDashoffset={141 - (141 * percentage / 100)}
              style={{ transition: 'stroke-dashoffset 1s ease-in-out' }}
            />
            {/* 중앙 텍스트 */}
            <text
              x={circleSize/2}
              y={circleSize/2 - 5}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="white"
              fontSize="22"
              fontWeight="bold"
            >
              {value.toFixed(1)}
            </text>
          </svg>
        </div>
        <div className="oee-subtitle">{subtitle}</div>
      </div>
    );
  };

  // 커스텀 툴팁
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{`${label}일`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.name}: ${entry.value}${entry.name === '생산량' ? '' : '%'}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <MainLayout>
      <div className="enterprise-container">
        {/* 헤더 */}
        <div className="enterprise-header">
          <div className="enterprise-header-left">
            <div className="enterprise-title">전사 통합 대시보드</div>
            <p className="enterprise-subtitle">회사 전체 생산 현황 및 운영 지표</p>
          </div>
          <div className="enterprise-header-right">
            <div className="report-buttons">
              <button className="report-btn daily" onClick={() => setIsDailyReportModalOpen(true)}>일일보고</button>
              <button className="report-btn weekly" onClick={() => setIsWeeklyReportModalOpen(true)}>주간보고</button>
              <button className="report-btn monthly" onClick={() => setIsMonthlyReportModalOpen(true)}>월간보고</button>
            </div>
            <div className="update-info" ref={refreshIndicatorRef}>
              <div className="update-label-container">
                <span className="update-label">업데이트</span>
                <span className="material-symbols-outlined refresh-icon">
                  sync
                </span>
              </div>
              <div className="update-time">
                {loading ? '로딩 중...' : (
                  dashboardData?.lastSyncTime
                    ? new Date(dashboardData.lastSyncTime).toLocaleString('ko-KR')
                    : lastUpdated.toLocaleTimeString('ko-KR')
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div style={{
            padding: '16px',
            marginBottom: '16px',
            backgroundColor: '#FEE2E2',
            border: '1px solid #EF4444',
            borderRadius: '8px',
            color: '#991B1B'
          }}>
            <strong>데이터 로드 실패:</strong> {error}
            <button
              onClick={loadDashboardData}
              style={{
                marginLeft: '12px',
                padding: '4px 12px',
                backgroundColor: '#EF4444',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              재시도
            </button>
          </div>
        )}

        {/* 상단 KPI 카드 */}
        <div className="kpi-grid">
          {/* 공장수 */}
          <div className="kpi-card">
            <div className="kpi-content">
              <div className="kpi-info">
                <div className="kpi-label">공장수</div>
                <div className="kpi-value blue">{Math.round(animatedTotalFactories)}</div>
                <div className="kpi-unit">개소</div>
              </div>
              <div className="kpi-icon blue">
                <span className="material-symbols-outlined">apartment</span>
              </div>
            </div>
          </div>

          {/* 전체설비수 */}
          <div className="kpi-card">
            <div className="kpi-content">
              <div className="kpi-info">
                <div className="kpi-label">전체설비수</div>
                <div className="kpi-value white">{Math.round(animatedTotalEquipment)}</div>
                <div className="kpi-unit" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ color: '#22C55E' }}>가동 {Math.round(animatedRunningEquipment)}</span>
                  <span style={{ color: '#F59E0B' }}>대기 {Math.round(animatedIdleEquipment)}</span>
                  <span style={{ color: '#EF4444' }}>정지 {Math.round(animatedStoppedEquipment)}</span>
                </div>
              </div>
              <div className="kpi-icon green">
                <span className="material-symbols-outlined" style={{ color: 'white' }}>precision_manufacturing</span>
              </div>
            </div>
          </div>

          {/* 총 가동률 */}
          <div className="kpi-card">
            <div className="kpi-content">
              <div className="kpi-info">
                <div className="kpi-label">총 가동률</div>
                <div className="kpi-value green">{animatedOperatingRate.toFixed(2)}%</div>
                <div className="kpi-unit">평균</div>
              </div>
              <div className="kpi-icon green">
                <span className="material-symbols-outlined">speed</span>
              </div>
            </div>
          </div>

          {/* 일일 생산량 */}
          <div className="kpi-card">
            <div className="kpi-content">
              <div className="kpi-info">
                <div className="kpi-label">일일 생산량</div>
                <div className="kpi-value yellow">{Math.round(animatedDailyProduction).toLocaleString()}</div>
                <div className="kpi-unit">EA</div>
              </div>
              <div className="kpi-icon yellow">
                <span className="material-symbols-outlined">bar_chart</span>
              </div>
            </div>
          </div>

          {/* 에너지 사용량 */}
          <div className="kpi-card">
            <div className="kpi-content">
              <div className="kpi-info">
                <div className="kpi-label">에너지사용량</div>
                <div className="kpi-value orange">{(animatedEnergyConsumption / 1000).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                <div className="kpi-unit">kWh</div>
              </div>
              <div className="kpi-icon orange">
                <span className="material-symbols-outlined">bolt</span>
              </div>
            </div>
          </div>
        </div>

        {/* 메인 콘텐츠 영역 */}
        <div className="main-grid">
          {/* 좌측: 종합 생산, 가동률 추이 + 공장별 현황 */}
          <div className="left-column">
            {/* 종합 생산, 가동률 추이 */}
            <div className="chart-card production-chart">
              <h3 className="chart-title">종합 생산 및 가동률 추이 (90일)</h3>

              <div className="chart-container-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={productionTrendData}
                    margin={{ top: -10, right: -20, left: -20, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis
                      dataKey="date"
                      stroke="#9CA3AF"
                      tick={{ fill: '#9CA3AF', fontSize: 11 }}
                      interval={9}
                      height={20}
                    />
                    <YAxis
                      yAxisId="left"
                      stroke="#9CA3AF"
                      tick={{ fill: '#9CA3AF', fontSize: 11 }}
                      domain={[0, 6000]}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      stroke="#F59E0B"
                      tick={{ fill: '#F59E0B', fontSize: 11 }}
                      domain={[70, 100]}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="top"
                      align="right"
                      wrapperStyle={{ paddingBottom: '10px', paddingRight: '20px' }}
                      iconType="square"
                      formatter={(value) => <span style={{ color: '#9CA3AF', fontSize: '12px' }}>{value}</span>}
                    />
                    <Bar yAxisId="left" dataKey="생산량" fill="#22C55E" opacity={0.7} />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="가동률"
                      stroke="#F59E0B"
                      strokeWidth={1}
                      dot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* 공장별 현황 */}
            <div className="chart-card factory-status">
              <div className="factory-status-header">
                <h3 className="chart-title">공장별 현황</h3>
                <button className="factory-detail-btn" onClick={() => navigate('/enterprise-factory-overview')}>
                  공장별 대시보드
                </button>
              </div>
              <div className="factory-grid">
                {factoryData.map((factory, index) => (
                  <div
                    key={index}
                    className="enterprise-factory-card"
                    onClick={() => handleFactoryClick(factory.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="factory-card-top">
                      {/* 공장명과 도넛차트 */}
                      <div className="factory-left">
                        <div className="factory-name-section">
                          <div className="enterprise-factory-name">{factory.name}</div>
                          <div className="factory-location">{factory.location}</div>
                        </div>
                        <DonutChart data={factory.equipment} />
                      </div>

                      {/* 설비 */}
                      <div className="factory-equipment-metric">
                        <div className="metric">
                          <div className="metric-label">설비</div>
                          <div className="metric-value white">{factory.equipment.total}</div>
                        </div>
                      </div>

                      {/* 구분선 */}
                      <div className="factory-vertical-divider"></div>

                      {/* 가동률, 생산량, 알람 + 환경 데이터 */}
                      <div className="factory-right-section">
                        <div className="factory-metrics-row">
                          <div className="metric">
                            <div className="metric-label">가동률</div>
                            <div className="metric-value green">{factory.operationRate}%</div>
                          </div>
                          <div className="metric">
                            <div className="metric-label">생산량</div>
                            <div className="metric-value yellow">{factory.production.toLocaleString()}</div>
                          </div>
                          <div className="metric">
                            <div className="metric-label">알람</div>
                            <div className="metric-value red">
                              {factory.alarms}
                            </div>
                          </div>
                        </div>
                        <div className="environment-divider"></div>
                        <div className="environment-metrics">
                          <div className="env-metric">
                            <span className="material-symbols-outlined env-icon temperature">thermostat</span>
                            <span className="env-value">{factory.environment.temperature}°C</span>
                          </div>
                          <div className="env-metric">
                            <span className="material-symbols-outlined env-icon humidity">humidity_percentage</span>
                            <span className="env-value">{factory.environment.humidity}%</span>
                          </div>
                          <div className="env-metric">
                            <span className="material-symbols-outlined env-icon air-quality">air</span>
                            <span className={`env-value ${factory.environment.airQuality > 35 ? 'red' : factory.environment.airQuality > 15 ? 'yellow' : 'green'}`}>
                              {factory.environment.airQuality}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 우측: 종합효율 OEE */}
          <div className="right-column">
            {/* OEE 2x2 카드 */}
            <div className="oee-grid">
              <SemiCircleGauge
                value={animatedOee}
                color="#3B82F6"
                title="종합 OEE"
                subtitle="Overall Equipment Effectiveness"
              />
              <SemiCircleGauge
                value={animatedAvailability}
                color="#22C55E"
                title="가용성"
                subtitle="Availability"
              />
            </div>

            <div className="oee-grid">
              <SemiCircleGauge
                value={animatedPerformance}
                color="#F59E0B"
                title="성능"
                subtitle="Performance"
                onSettingsClick={handleOpenModal}
              />
              <SemiCircleGauge
                value={animatedQuality}
                color="#8B5CF6"
                title="품질"
                subtitle="Quality"
              />
            </div>

            {/* OEE 추이 그래프 */}
            <div className="chart-card oee-trend">
              <div className="oee-trend-header">
                <h4 className="chart-title">OEE 추이 (90일)</h4>
                <div className="oee-legend">
                  <div className="legend-item">
                    <div className="legend-line blue"></div>
                    <span>종합 OEE</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-line green"></div>
                    <span>가용성</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-line yellow"></div>
                    <span>성능</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-line purple"></div>
                    <span>품질</span>
                  </div>
                </div>
              </div>

              <div className="oee-chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={oeeMonthlyData}
                    margin={{ top: 5, right: 5, left: -25, bottom: -5 }}
                  >
                    <defs>
                      <linearGradient id="colorOEE" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.05}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis
                      dataKey="date"
                      stroke="#9CA3AF"
                      tick={{ fill: '#9CA3AF', fontSize: 11 }}
                      interval={9}
                    />
                    <YAxis
                      stroke="#9CA3AF"
                      tick={{ fill: '#9CA3AF', fontSize: 11 }}
                      domain={[10, 100]}
                      ticks={[10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="OEE"
                      stroke="none"
                      fill="url(#colorOEE)"
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="OEE"
                      stroke="#3B82F6"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="가용성"
                      stroke="#22C55E"
                      strokeWidth={1}
                      strokeDasharray="5 3"
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="성능"
                      stroke="#F59E0B"
                      strokeWidth={1}
                      strokeDasharray="5 3"
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="품질"
                      stroke="#8B5CF6"
                      strokeWidth={1}
                      strokeDasharray="5 3"
                      dot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* 목표 생산량 설정 모달 */}
        <TargetProductionModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveTargetProduction}
          currentValue={currentTargetProduction}
        />

        {/* 일일보고서 모달 */}
        <DailyReportModal
          isOpen={isDailyReportModalOpen}
          onClose={() => setIsDailyReportModalOpen(false)}
        />

        {/* 주간보고서 모달 */}
        <WeeklyReportModal
          isOpen={isWeeklyReportModalOpen}
          onClose={() => setIsWeeklyReportModalOpen(false)}
        />

        {/* 월간보고서 모달 */}
        <MonthlyReportModal
          isOpen={isMonthlyReportModalOpen}
          onClose={() => setIsMonthlyReportModalOpen(false)}
        />
      </div>
    </MainLayout>
  );
}

export default Enterprise;
